import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { agentInstanceService } from "../../../services/agentService";

interface Props {
    agent: any;
}

const AISettingsTab: React.FC<Props> = ({ agent }) => {
    const [settings, setSettings] = useState({
        model: 'GPT-4',
        temperature: 0.7,
        maxTokens: 1000,
        systemPrompt: 'You are a helpful customer support agent...',
        responseStyle: 'professional',
        enableContextMemory: true,
        maxContextLength: 10,
    });
    const [saving, setSaving] = useState(false);

    console.log(agent?.version, "agent in ai setting")
    useEffect(() => {
        if (!agent) return;
        const ai = agent.aiSettings ?? {};
        const s = ai.settings ?? ai;

        setSettings(prev => ({
            ...prev,
            model: s.model ?? prev.model,
            temperature: typeof s.temperature === 'number' ? s.temperature : prev.temperature,
            maxTokens: typeof s.maxOutputTokens === 'number' ? s.maxOutputTokens :
                (typeof s.maxTokens === 'number' ? s.maxTokens : prev.maxTokens),
            systemPrompt: s.systemPrompt ?? prev.systemPrompt,
            responseStyle: s.responseStyle ?? prev.responseStyle,
            enableContextMemory: !!(s.contextMemory?.enabled ?? prev.enableContextMemory),
            maxContextLength: typeof s.contextMemory?.maxTurns === 'number'
                ? s.contextMemory.maxTurns
                : (typeof s.maxContextLength === 'number' ? s.maxContextLength : prev.maxContextLength)
        }));
    }, [agent]);


    const handleSettingChange = (key: string, value: any) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const validate = () => {
        if (!settings.model) return 'Model is required.';
        if (settings.maxTokens <= 0) return 'Max tokens must be greater than 0.';
        if (settings.temperature < 0 || settings.temperature > 1) return 'Temperature must be between 0 and 1.';
        if (settings.enableContextMemory && (!Number.isInteger(settings.maxContextLength) || settings.maxContextLength < 1)) return 'Max context length must be a positive integer.';
        return null;
    };

    const handleSave = async () => {
        const err = validate();
        if (err) {
            toast.error(err);
            return;
        }


        if (!agent || !agent.id) {
            toast.error('Agent id missing. Cannot save settings.');
            return;
        }


        setSaving(true);
        const payload = {
            schemaVersion: Number(agent?.version) || 1,
            settings: {
                model: settings.model,
                temperature: settings.temperature,
                maxOutputTokens: settings.maxTokens,
                systemPrompt: settings.systemPrompt,
                responseStyle: settings.responseStyle,
                contextMemory: {
                    enabled: settings.enableContextMemory,
                    maxTurns: settings.maxContextLength
                }
            }
        };


        try {
            await agentInstanceService.updateAgentInstanceAiSettings(encodeURIComponent(agent.id), payload);
            toast.success('AI settings saved successfully.');
        } catch (e: any) {
            console.error('Failed to save AI settings', e);
            const msg = e?.message ?? 'Failed to save settings. Please try again.';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="p-6">
            <h3 className="text-lg font-semibold text-agentic-text-primary mb-6">AI Model Configuration</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-agentic-text-secondary mb-2">Model</label>
                        <select
                            value={settings.model}
                            onChange={(e) => handleSettingChange('model', e.target.value)}
                            className="w-full px-3 py-2 border border-agentic-gray-300 rounded-lg focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                        >
                            <option value="gpt-4o-mini">gpt-4o-mini</option>
                            {/* <option value="GPT-4">GPT-4</option>
                            <option value="GPT-3.5-turbo">GPT-3.5 Turbo</option>
                            <option value="Claude-3">Claude 3</option>
                            <option value="Gemini-Pro">Gemini Pro</option> */}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-agentic-text-secondary mb-2">Temperature: {settings.temperature}</label>
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={settings.temperature}
                            onChange={(e) => handleSettingChange('temperature', parseFloat(e.target.value))}
                            className="w-full"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-agentic-text-secondary mb-2">Max Tokens</label>
                        <input
                            type="number"
                            value={settings.maxTokens}
                            onChange={(e) => handleSettingChange('maxTokens', parseInt(e.target.value || '0'))}
                            className="w-full px-3 py-2 border border-agentic-gray-300 rounded-lg focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-agentic-text-secondary mb-2">Response Style</label>
                        <select
                            value={settings.responseStyle}
                            onChange={(e) => handleSettingChange('responseStyle', e.target.value)}
                            className="w-full px-3 py-2 border border-agentic-gray-300 rounded-lg focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                        >
                            <option value="professional">Professional</option>
                            <option value="friendly">Friendly</option>
                            <option value="formal">Formal</option>
                            <option value="casual">Casual</option>
                        </select>
                    </div>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-agentic-text-secondary mb-2">Instructions</label>
                        <textarea
                            value={settings.systemPrompt}
                            onChange={(e) => handleSettingChange('systemPrompt', e.target.value)}
                            rows={6}
                            className="w-full px-3 py-2 border border-agentic-gray-300 rounded-lg focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                            placeholder="Enter the system prompt that defines the agent's behavior..."
                        />
                    </div>
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="contextMemory"
                            checked={settings.enableContextMemory}
                            onChange={(e) => handleSettingChange('enableContextMemory', e.target.checked)}
                            className="h-4 w-4 text-agentic-primary-blue focus:ring-agentic-primary-blue border-agentic-gray-300 rounded"
                        />
                        <label htmlFor="contextMemory" className="ml-2 block text-sm text-agentic-text-secondary">
                            Enable Context Memory
                        </label>
                    </div>
                    {settings.enableContextMemory && (
                        <div>
                            <label className="block text-sm font-medium text-agentic-text-secondary mb-2">Max Context Length</label>
                            <input
                                type="number"
                                value={settings.maxContextLength}
                                onChange={(e) => handleSettingChange('maxContextLength', parseInt(e.target.value || '0'))}
                                className="w-full px-3 py-2 border border-agentic-gray-300 rounded-lg focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="mt-6 flex justify-end">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className={`px-6 py-2 text-white rounded-lg transition-colors ${saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-agentic-primary-blue hover:bg-agentic-primary-blue/90'}`}
                >
                    {saving ? 'Saving...' : 'Save Settings'}
                </button>
            </div>
        </div>
    );
};

export default AISettingsTab;
